import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  createErrorHandler,
  ErrorMessages,
  ErrorResponse,
} from '../common/utils/errorHandler';

export interface FormErrorHandler {
  /** Current top-level server error message, or null. */
  serverError: string | null;
  setServerError: (message: string | null) => void;
  /** Clear the current server error. */
  clearError: () => void;
  /**
   * Run an API error through the shared ErrorHandler. Stores `server` errors in
   * `serverError` and returns the full mapping so callers can also surface
   * field-specific (email/password) errors when needed.
   */
  handleApiError: (error: unknown) => ErrorResponse;
}

/**
 * Consolidates the `serverError` state + `ErrorHandler` instantiation that was
 * duplicated across the auth forms. Decoupled from the class's old
 * hook-in-constructor via `createErrorHandler(t)`.
 */
export function useFormErrorHandler(
  messages: ErrorMessages = {},
): FormErrorHandler {
  const { t } = useTranslation();
  const [serverError, setServerError] = useState<string | null>(null);

  const handleApiError = (error: unknown): ErrorResponse => {
    // Cheap to build; messages are per-call literals so no memo is needed.
    const handled = createErrorHandler(t, messages).handleError(error);
    if ('server' in handled && handled.server) setServerError(handled.server);
    return handled;
  };

  return {
    serverError,
    setServerError,
    clearError: () => setServerError(null),
    handleApiError,
  };
}
